import { useApiClient } from "@/composables/useApiClient";
import type {
  SchoolClassCreateInput,
  SchoolClassUpdateInput,
} from "@/services/fobizzToolsApi";

const PAGINATION_LIMIT = 4;

export function useSchoolClassQueries() {
  const queryClient = useQueryClient();
  const { schoolClasses: schoolClassesService } = useApiClient();

  const useIndexQuery = (
    {
      discarded,
      page,
      limit,
    }: {
      discarded?: Ref<boolean>;
      page?: Ref<number>;
      limit?: number;
    },
    opts = {}
  ) => {
    return useQuery(
      {
        queryKey: ["schoolClasses", discarded, page],
        queryFn: () =>
          schoolClassesService.listSchoolClasses({
            discarded:
              discarded && discarded.value
                ? discarded.value.toString()
                : "false",
            page: (page && page.value) || 1,
            limit: limit || PAGINATION_LIMIT,
          }),
        placeholderData: keepPreviousData,
        ...opts,
      },
      queryClient
    );
  };

  const getQuery = (id: string, opts = {}) => {
    return useQuery(
      {
        queryKey: ["schoolClasses", id],
        queryFn: () => schoolClassesService.getSchoolClass({ id: id }),
        ...opts,
      },
      queryClient
    );
  };

  const useUpdateMutation = (id: string) => {
    return useMutation(
      {
        mutationFn: (attrs: SchoolClassUpdateInput) =>
          schoolClassesService.updateSchoolClass({
            id,
            requestBody: { schoolClass: attrs },
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["schoolClasses", id],
          });
          queryClient.invalidateQueries({
            queryKey: ["pupils", id],
          });
        },
      },
      queryClient
    );
  };

  const useCreateMutation = () => {
    return useMutation(
      {
        mutationFn: (attrs: SchoolClassCreateInput) =>
          schoolClassesService.createSchoolClass({
            requestBody: { schoolClass: attrs },
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["schoolClasses"],
          });
          queryClient.invalidateQueries({
            queryKey: ["userLimit"],
          });
        },
      },
      queryClient
    );
  };

  const useDeleteMutation = () => {
    return useMutation(
      {
        mutationFn: (id: string) =>
          schoolClassesService.deleteSchoolClass({
            id,
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["schoolClasses"],
          });
          queryClient.invalidateQueries({
            queryKey: ["userLimit"],
          });
        },
      },
      queryClient
    );
  };

  return {
    useIndexQuery,
    getQuery,
    useUpdateMutation,
    useCreateMutation,
    useDeleteMutation,
  };
}
